import { useContext, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { colors } from '@karnott/colors';
import { DriverIcon } from '@karnott/icons';
import { LOCALE_FORMAT } from '../../../../constants';
import { I18nContext } from '../../../../contexts/I18nProvider';
import { UIContext } from '../../../../contexts/UIProvider';
import { I18n } from '../../../../i18n';
import { ANALYTICS, sendAnalyticsTooltipClickEvent } from '../../../../utils/AnalyticsConstants';
import { getEquipmentIcon } from '../../../../utils/equipments';
import { getUserName } from '../../../../utils/user';
import { getEquipmentFromWorksite } from '../../../../utils/worksites';
import { useDrawMarkerOnMap } from '../../effects';
import { openWorksiteTooltip } from './events';

function filterUniqueUserSessions(userSessions) {
  return userSessions.reduce((acc, user) => {
    const userId = user.user_id;
    if (!acc.some((u) => u.user_id === userId)) {
      acc.push(user);
    }
    return acc;
  }, []);
}

export function useDrawWorksitesOnMap(map, worksite, events, hideWorksiteTooltip) {
  const { focusedWorksiteId } = useContext(UIContext);
  const { t } = useContext(I18nContext);
  const history = useHistory();
  const isWorksiteActive = useMemo(() => worksite.device_session_status === 'active', [worksite]);
  const [eqLatitude, eqLongitude] = useMemo(
    () => [worksite.equipmentLatitude, worksite.equipmentLongitude],
    [worksite.equipmentLatitude, worksite.equipmentLongitude],
  );
  // Worksite can be focus by WorksitesDatas(Dashboard)
  const focused = useMemo(() => {
    return (
      focusedWorksiteId &&
      (focusedWorksiteId === worksite.device_session_id ||
        worksite.equipment_instances.find((ei) => ei?.id === focusedWorksiteId))
    );
  }, [focusedWorksiteId, worksite]);

  const { location, Icons, iconColor, backgroundColor } = useMemo(() => {
    const Icons = [];
    (worksite?.equipment_instances || []).forEach((wei) => {
      Icons.push(wei?.equipmentModel ? getEquipmentIcon(wei.equipmentModel.type) : getEquipmentIcon('misc'));
    });
    if (worksite?.related_user_sessions) {
      // need filter related_user_sessions to remove duplicates
      const uniqueUserSessions = filterUniqueUserSessions(worksite.related_user_sessions);
      uniqueUserSessions.forEach(() => Icons.push(DriverIcon));
    }
    return {
      location: { latitude: eqLatitude, longitude: eqLongitude },
      Icons: Icons,
      iconColor: colors('white'),
      backgroundColor: focused ? colors('black', 'dark') : isWorksiteActive ? colors('green') : colors('grey', 'light'),
    };
  }, [eqLatitude, eqLongitude, focused, isWorksiteActive, worksite]);
  const marker = useDrawMarkerOnMap(
    map,
    { ...worksite, color: getEquipmentFromWorksite(worksite)?.color },
    location,
    Icons,
    iconColor,
    backgroundColor,
    events,
    undefined,
    undefined,
  );
  const openedTooltipLayer = useRef(null);
  const labels = useWorksiteTooltipLabels();
  const worksiteProps = useMemo(() => worksiteTooltipData(worksite, history), [history, worksite]);

  useEffect(() => {
    const { layerGroup, iconMarker, backgroundMarker } = marker;
    iconMarker?.setZIndexOffset(9000);
    backgroundMarker?.setZIndexOffset(9000);

    if (
      !focusedWorksiteId ||
      (focusedWorksiteId !== worksite.device_session_id &&
        !worksite.equipment_instances?.find((ei) => ei.id === focusedWorksiteId)) ||
      !layerGroup ||
      !iconMarker ||
      !backgroundMarker
    )
      return () => {};

    openedTooltipLayer.current = marker;
    !hideWorksiteTooltip && queueMicrotask(() => openWorksiteTooltip(worksiteProps, iconMarker, labels));
    iconMarker.setZIndexOffset(10000);
    backgroundMarker.setZIndexOffset(10000);

    return () => {
      if (openedTooltipLayer.current) {
        openedTooltipLayer.current.iconMarker.closeTooltip();
        iconMarker.setZIndexOffset(9000);
        backgroundMarker.setZIndexOffset(9000);
        openedTooltipLayer.current = null;
      }
    };
  }, [focusedWorksiteId, labels, marker, openedTooltipLayer, Icons, t, worksiteProps, hideWorksiteTooltip, worksite]);
}

export function useWorksiteTooltipLabels() {
  const { t } = useContext(I18nContext);
  return useMemo(
    () => ({
      in_progress: t('Commons.in_progress'),
      from_date: t('Commons.start'),
      to_date: t('Commons.end'),
      last_update: t('Dashboard.last_actualization'),
    }),
    [t],
  );
}

export function worksiteTooltipData(worksite = {}, history, active = true) {
  const Icons = [],
    actions = [];
  (worksite.equipment_instances || []).forEach((wei) => {
    const actionCB = () => {
      sendAnalyticsTooltipClickEvent(window)(ANALYTICS.LABEL.TOOLTIPS.WORKSITE);
      history.push(`/equipments/${wei.id}${wei.equipmentSessionId ? `/sessions/${wei.equipmentSessionId}` : ''}`);
    };
    const eqIcon = wei?.equipmentModel ? getEquipmentIcon(wei?.equipmentModel.type) : getEquipmentIcon('misc');
    const action = { name: wei?.label || '-', actionCB: actionCB };
    Icons.push(eqIcon);
    actions.push(action);
  });
  // need filter related_user_sessions to remove duplicates
  if (worksite.related_user_sessions) {
    const uniqueUserSessions = filterUniqueUserSessions(worksite.related_user_sessions);
    uniqueUserSessions.forEach((user) => {
      Icons.push(DriverIcon);
      actions.push({
        icon: DriverIcon,
        name: getUserName(user),
        actionCB: () => {
          history.push(`/drivers/${user.user_id}`);
        },
      });
    });
  }

  const equipment = (worksite.equipment_instances || []).find((e) => !!e.activeDevice);

  return {
    Icons: Icons,
    worksiteType: I18n.t(`WorkType.${worksite.equipment_model}`),
    fromDate: moment(worksite.from_date).format(LOCALE_FORMAT),
    isInProgress: worksite.device_session_status === 'active',
    lastUpdate: active
      ? moment(equipment?.activeDevice?.locationDate || worksite.to_date).format(LOCALE_FORMAT)
      : undefined,
    toDate: active ? undefined : moment(worksite.to_date).format(LOCALE_FORMAT),
    actions: actions,
    history,
  };
}
